/**
 * @file   src\Pages\MembershipReport.tsx
 * @brief  Membership Data Report page for each carwash.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
  Container,
  Table,
  Badge,
  Breadcrumb,
  useNavigate,
  useEffect,
  useOutletContext,
  useState,
  Button,
  useParams,
  Pagination,
  React,
} from "../utils/thirdpartyComponents";
import { userType } from "../utils/enums";
import { pageURLs } from "../utils/pageUrls";
import { AppContextType } from "../interfaces/generalInterface";
import axios from "../config/axiosConfig";
import { CompanyApi, RevenueApi, LocationApi } from "../utils/apiUrls";
import SmallLoader from "../components/SmallLoader";
import CWSelect from "../components/CWSelect";
import { MessageToaster } from "../utils/toastUtil";
import { LocationItem, Option } from "../interfaces/subscriptionInterface";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

interface MembershipRecord {
  TotalRows?: number;
  UserID?: number;
  FullName?: string;
  UserStatus?: string;
  LocationName?: string;
  PackageName?: string;
  PackageCost?: number;
  "SignUp Date"?: string;
  NextPaymentDate?: string;
  CardExpiryMonth?: number | null;
  CardExpiryYear?: number | null;
  FamilyMember?: number;
}

const MembershipReport = (props: any) => {
  // Access the context
  const { updateData } = useOutletContext<AppContextType>();
  const carwash_owner =
    props.userType === userType["CARWASH-OWNER"] ? true : false;

  const navigate = useNavigate();

  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [reportRecords, setReportRecords] = useState<MembershipRecord[]>([]);
  const [locations, setLocations] = useState<Option[]>([]);
  const [filters, setFilters] = useState({
    locationId: "0", // "0" represents "All"
  });
  const [reportGenerated, setReportGenerated] = useState(false);
  
  const toaster = new MessageToaster();
  const timezoneOffset = new Date().getTimezoneOffset();

  // State to company details
  const [company, setCompany] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const pageSize = 10;

  // To display heading
  useEffect(() => {
    getCompany();
    getCompanyAddress();
    getCompanyLocations();
  }, []);

  useEffect(() => {
    handleUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    // Only load data when currentPage changes AND report has been generated
    if (reportGenerated) {
      getMembershipReport(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const getCompanyAddress = async () => {
    try {
      const id = params.id ? parseInt(params.id) : 0;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
        : await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id);
      if (data?.error === 0) {
        setCompany(data.data);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const getCompanyLocations = async () => {
    try {
      const carwashGuid = params.id || "";
      const { data } = carwash_owner
        ? await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS)
        : await axios.get(LocationApi.GET_COMPANY_LOCATIONS + carwashGuid);
      if (data.error === 0) {
        const location_list: LocationItem[] = data?.data;

        // Transform the array
        const transformedArray: Option[] = location_list.map((item) => ({
          label: item.locationName,
          value: item.locationId,
        }));

        // Add "All" option at the beginning (using 0 as value for "all")
        setLocations([{ label: "All", value: 0 }, ...transformedArray]);
      } else {
        toaster.toastError(data.message);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const handleFilterChange = (name: string, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Update the data
  const handleUpdate = () => {
    updateData({
      heading: "Membership Data Report",
      url: carwash_owner
        ? ""
        : `/superadmin/${pageURLs.carwash_locations}/${params.id}`,
      showBackButton:
        props.userType === userType["CARWASH-OWNER"] ? false : true,
      showMainMenu: true,
      activeLink:
        props.userType === userType["CARWASH-OWNER"]
          ? "reports"
          : "manage-car-wash",
    });
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "";
    try {
      return moment(dateString).format("MM/DD/YYYY");
    } catch (error) {
      return dateString;
    }
  };

  const formatCreditCardExpiry = (month: number | null | undefined, year: number | null | undefined) => {
    if (!month || !year) return "";
    // Format as MM/YY
    const formattedMonth = month.toString().padStart(2, '0');
    const formattedYear = year.toString().slice(-2);
    return `${formattedMonth}/${formattedYear}`;
  };

  const formatFamilyMember = (familyMember: number | null | undefined) => {
    if (familyMember === 1) return "Yes";
    return "No";
  };

  const getUserStatusMeta = (userStatus: string | null | undefined) => {
    const normalizedStatus = (userStatus ?? "").toLowerCase().trim();

    if (normalizedStatus === "activeuser") {
      return { label: "Active", className: "active" };
    }

    if (normalizedStatus === "cancelled") {
      return { label: "Canceled", className: "inactive" };
    }

    return { label: "Inactive", className: "inactive" };
  };

  // Common function to fetch report data from API
  const fetchReportData = async (pageNo: number, pageSizeParam: number) => {
    const queryParams = new URLSearchParams();

    // pagination
    const offset = timezoneOffset;
    queryParams.append("offset", offset.toString());
    queryParams.append("pageNo", pageNo.toString());
    queryParams.append("pageSize", pageSizeParam.toString());

    // required carwash
    if (params.id) {
      queryParams.append("carWashId", params.id.toString());
    }

    // filters - only include locationId if it's not "0" (All)
    if (filters.locationId && filters.locationId !== "0") {
      queryParams.append("locationId", filters.locationId);
    }

    const apiUrl = RevenueApi.CARWASH_OWNER_MEMBERSHIP_REPORT;
    
    const url = `${apiUrl}?${queryParams.toString()}`;
    const { data } = await axios.get(url);

    if (data?.error === 0) {
      // Check if data.data is an array or has a list property
      let records = [];
      let totalCount = 0;
      
      if (Array.isArray(data?.data)) {
        records = data.data;
        totalCount = data?.TotalRows || records.length;
      } else if (data?.data?.list && Array.isArray(data.data.list)) {
        records = data.data.list;
        totalCount = data.data.totalCount || data?.TotalRows || records.length;
      } else if (Array.isArray(data)) {
        records = data;
        totalCount = records.length;
      }
      
      const limit = data?.data?.limit || pageSizeParam || pageSize;
      
      return {
        list: records,
        totalCount: totalCount,
        limit: limit
      };
    }
    
    return null;
  };

  const getMembershipReport = async (pageNo = 1) => {
    try {
      setLoading(true);
      const result = await fetchReportData(pageNo, pageSize);

      if (result) {
        if (result.list?.length > 0) {
          setReportRecords(result.list);
          setTotalPages(Math.ceil(result.totalCount / result.limit) || 1);
        } else {
          setReportRecords([]);
        }
        setReportGenerated(true); // Mark that report has been generated
      } else {
        setReportRecords([]);
        setReportGenerated(true);
      }
    } catch (error: any) {
      console.error("Error fetching membership report:", error.message);
      toaster.toastError(error.response?.data?.message || error.message || "Failed to fetch report");
      setReportRecords([]);
      setReportGenerated(true);
    } finally {
      setLoading(false);
    }
  };

  const downloadReportAsExcel = async () => {
    try {
      setLoading(true);
      const result = await fetchReportData(1, 0); // pageNo = 1, pageSize = 0 to get all records

      if (result && result.list?.length > 0) {
        const worksheetData = result.list.map((item: any) => ({
          "User Name": item?.FullName ?? "",
          "User Status": getUserStatusMeta(item?.UserStatus).label,
          "Location Name": item?.LocationName ?? "",
          "Package Name": item?.PackageName ?? "",
          "Package Cost ($/month)": item?.PackageCost ?? 0,
          "Sign-Up Date": formatDate(item?.["SignUp Date"]),
          "Last Payment Date": formatDate(item?.LastPaymentDate),
          "Next Payment Date": formatDate(item?.NextPaymentDate),
          "Credit Card Expiry Date": formatCreditCardExpiry(item?.CardExpiryMonth, item?.CardExpiryYear),
          "Family Plan Member": formatFamilyMember(item?.FamilyMember),
        }));

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "MembershipDataReport");

        const workbookArray = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const blob = new Blob([workbookArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const fileName = `MembershipDataReport_${moment().format("YYYY-MM-DD")}.xlsx`;
        saveAs(blob, fileName);
      } else {
        toaster.toastError("No records to download.");
      }
    } catch (error: any) {
      console.error("Error downloading membership report:", error.message);
      toaster.toastError(error.response?.data?.message || error.message || "Failed to download report");
    } finally {
      setLoading(false);
    }
  };

  // Navigate according to url
  const itemClick = (url: string) => {
    navigate(url);
  };

  const getVisiblePageNumbers = () => {
    const totalPageNumbersToShow = 5;
    let startPage = Math.max(
      1,
      currentPage - Math.floor(totalPageNumbersToShow / 2)
    );
    let endPage = startPage + totalPageNumbersToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Function to fetch company information
  const getCompany = async () => {
    try {
      const param1 = `${params.id}`;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS)
        : await axios.get(CompanyApi.GET_COMPANY + param1);
    } catch (error: any) {
      console.error("Error fetching company information:", error.message);
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  return (
    <div className="manage-carwash-container order-list-container multi-location-wrap">
      <Container>
        <Breadcrumb>
          {carwash_owner ? (
            <>
              <Breadcrumb.Item
                onClick={() => itemClick(`/${pageURLs.dashboard}`)}
              >
                Dashboard
              </Breadcrumb.Item>
            </>
          ) : (
            <>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(`/superadmin/${pageURLs.manage_carwash}`);
                }}
              >
                Manage Car Washes
              </Breadcrumb.Item>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(
                    `/superadmin/${pageURLs.carwash_locations}/${params.id}`
                  );
                }}
              >
                {company.companyName}
              </Breadcrumb.Item>
            </>
          )}
          <Breadcrumb.Item active>Membership Data Report</Breadcrumb.Item>
        </Breadcrumb>

        <div className="manage-header-block d-flex justify-content-between align-items-center">
          <div className="search-wrap multi-location-action w-100">
            <div className="result-group d-flex align-items-center">
              <CWSelect
                id="selectlocation"
                name="selectlocation"
                options={locations}
                label="Select Location"
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select Location"
                onChange={(selected: any) => {
                  const value = Array.isArray(selected) && selected.length > 0 ? selected[0].value : "";
                  handleFilterChange("locationId", value.toString());
                  setCurrentPage(1); // Reset to first page when location changes
                  setReportGenerated(false); // Reset report flag so user needs to click Run Report again
                  setReportRecords([]); // Clear existing records
                }}
              />
              <div className="button-group d-flex">
                <Button
                  className="primary-btn"
                  variant="primary"
                  title="Run Report"
                  onClick={() => {
                    setCurrentPage(1);
                    getMembershipReport(1);
                  }}
                >
                  Run Report
                </Button>
                <Button
                  className="secondary-btn ms-3"
                  variant="outline-primary"
                  title="Export Report"
                  onClick={downloadReportAsExcel}
                >
                  Export Report
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="cw-table-wrap report-table">
          <Table responsive className="m-0">
            <thead>
              <tr>
                <th>User Name</th>
                <th>User Status</th>
                <th>Location Name</th>
                <th>Package Name</th>
                <th>Package Cost ($/month)</th>
                <th>Sign-Up Date</th>
                <th>Last Payment Date</th>
                <th>Next Payment Date</th>
                <th>Credit Card Expiry Date</th>
                <th>Family Plan Member</th>
              </tr>
            </thead>
            <tbody>
              {reportRecords.length > 0 ? (
                reportRecords.map((item: any, index) => {
                  const userStatus = getUserStatusMeta(item?.UserStatus);

                  return (
                    <tr key={index}>
                      <td>{item?.FullName ?? ""}</td>
                      <td>
                        <Badge className={userStatus.className}>
                          {userStatus.label}
                        </Badge>
                      </td>
                      <td>{item?.LocationName ?? ""}</td>
                      <td>{item?.PackageName ?? ""}</td>
                      <td>${(item?.PackageCost ?? 0).toFixed(2)}</td>
                      <td>{formatDate(item?.["SignUp Date"])}</td>
                      <td>{formatDate(item?.LastPaymentDate)}</td>
                      <td>{formatDate(item?.NextPaymentDate)}</td>
                      <td>{formatCreditCardExpiry(item?.CardExpiryMonth, item?.CardExpiryYear)}</td>
                      <td>{formatFamilyMember(item?.FamilyMember)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={11} className="text-center">
                    {loading ? "Loading..." : "No records found"}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {reportRecords && reportRecords?.length ? (
            <div className="mt-3 d-flex justify-content-end">
              <Pagination>
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                />
                {getVisiblePageNumbers().map((pageNumber) => (
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === currentPage}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages)}
                />
              </Pagination>
            </div>
          ) : null}
        </div>
      </Container>

      {loading && <SmallLoader />}
    </div>
  );
};

export default MembershipReport;

